import { getFilteredElements, hydrateFilters } from 'src/utils/FilterUtils';
import { ref } from 'vue';

import {
  createCareModelCohortAssignmentReview,
} from '../../application/useCases/createCareModelCohortAssignmentReview';
import { assignClientsToCohorts } from '../../infrastructure/api/careModelClientAssignmentsApi';

/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').CareModelCohortAssignmentReview
 * } CareModelCohortAssignmentReview */
/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').CareModelCohortClientAssignment
 * } CareModelCohortClientAssignment */
/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').SuggestedCareModelCohortAssignment
 * } SuggestedCareModelCohortAssignment */
/** @typedef {
 *   import('../../domain/entities/CareModelCohortAssignment.types').ManualCareModelCohortAssignment
 * } ManualCareModelCohortAssignment */
/** @typedef {
 *   import(
 *    '../../application/useCases/createCareModelCohortAssignmentReview.types'
 *   ).PrepareCareModelCohortAssignmentReviewParams
 * } PrepareCareModelCohortAssignmentReviewParams */
/** @typedef {
 *   import('../../application/useCases/createCareModelCohortAssignmentReview.types').AssignCareModelCohortClientsParams
 * } AssignCareModelCohortClientsParams */

// eslint-disable-next-line import/prefer-default-export
export function useCareModelReviewAssignments() {
  const isProcessing = ref(false);
  const suggestedAssignments = ref([]);
  const manualAssignments = ref([]);

  const reviewUseCase = createCareModelCohortAssignmentReview(
    assignClientsToCohorts,
    hydrateFilters,
    getFilteredElements,
  );

  /**
   * @param {PrepareCareModelCohortAssignmentReviewParams} params
   * @returns {CareModelCohortAssignmentReview}
   */
  function prepareReview(params) {
    const review = reviewUseCase.prepareReview(params);
    suggestedAssignments.value = review.suggestedAssignments;
    manualAssignments.value = review.manualAssignments;
    return review;
  }

  function toggleAllSuggested(checked) {
    suggestedAssignments.value = suggestedAssignments.value.map((item) => ({
      ...item,
      selected: !!checked,
    }));
  }

  function toggleSuggestedRow(clientId, checked) {
    suggestedAssignments.value = suggestedAssignments.value.map((item) => {
      if (item.client.id !== clientId) {
        return item;
      }

      return {
        ...item,
        selected: !!checked,
      };
    });
  }

  function updateManualSelection(clientId, cohortId) {
    manualAssignments.value = manualAssignments.value.map((item) => {
      if (item.client.id !== clientId) {
        return item;
      }

      return {
        ...item,
        selectedCohortId: cohortId || null,
      };
    });
  }

  /**
   * @returns {CareModelCohortClientAssignment[]}
   */
  function collectAssignments() {
    return reviewUseCase.collectAssignments({
      suggestedAssignments: suggestedAssignments.value,
      manualAssignments: manualAssignments.value,
    });
  }

  /**
   * @param {AssignCareModelCohortClientsParams} params
   * @returns {Promise<void>}
   */
  async function assignClients(params) {
    isProcessing.value = true;
    try {
      return await reviewUseCase.assignClients(params);
    } finally {
      isProcessing.value = false;
    }
  }

  return {
    isProcessing,
    suggestedAssignments,
    manualAssignments,
    prepareReview,
    toggleAllSuggested,
    toggleSuggestedRow,
    updateManualSelection,
    collectAssignments,
    assignClients,
  };
}
